import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import axios from 'axios';
import { toast } from "react-toastify";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { FiSend, FiXCircle, FiClock, FiPlus, FiArrowLeft, FiEye } from "react-icons/fi";
import dayjs from 'dayjs';

function TournamentMessages({ show, onHide, tournament, participants, participant }) {
    const [view, setView] = useState('list'); // 'list', 'compose', 'detail'
    const [messages, setMessages] = useState([]);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [showArchived, setShowArchived] = useState(false);

    // Compose State
    const [loading, setLoading] = useState(false);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [newEmail, setNewEmail] = useState({ subject: '', content: '' });
    const [selectedTemplate, setSelectedTemplate] = useState('new');
    const [useTemplate, setUseTemplate] = useState(false);
    const [selectedParticipant, setSelectedParticipant] = useState('all');
    const [isRecipientInternal, setIsRecipientInternal] = useState(false);

    const quillRef = useRef(null);

    const placeholderMap = [
        { placeholder: '{{firstname}}', alias: 'Vorname' },
        { placeholder: '{{lastname}}', alias: 'Nachname' },
        { placeholder: '{{teamname}}', alias: 'Teamname' },
        { placeholder: '{{paid}}', alias: 'Bezahlstatus' },
        { placeholder: '{{t_name}}', alias: 'Turniername' },
        { placeholder: '{{t_location}}', alias: 'Ort' },
        { placeholder: '{{t_time}}', alias: 'hh:mm Uhr' },
        { placeholder: '{{t_time1}}', alias: 'hh:mm' },
        { placeholder: '{{t_prize}}', alias: 'Gebühr' },
        { placeholder: '{{t_info}}', alias: 'Infotext' },
        { placeholder: '{{t_link}}', alias: 'Link(Button)' },
        { placeholder: '{{t_signup}}', alias: 'Anmelde-Button' },
        { placeholder: '{{t_players}}', alias: 'Spieler' },
        { placeholder: '{{t_teams}}', alias: 'Teams' },
        { placeholder: '{{t_fieldplayers}}', alias: 'Feldpieler' },
        { placeholder: '{{t_date}}', alias: 'DD.MM.YYYY' },
        { placeholder: '{{t_date1}}', alias: 'dd.Monat JJJJ' },
        { placeholder: '{{t_date2}}', alias: 'Tag, dd.Monat JJJJ' },
    ];

    const stripHtml = (html) => { if (!html) return ""; let tmp = document.createElement("DIV"); tmp.innerHTML = html; return tmp.textContent || tmp.innerText || ""; };

    useEffect(() => {
        if (show) {
            fetchMessages();
            fetchEmailTemplates();
            const initialParticipantId = participant?._id || 'all';
            setSelectedParticipant(initialParticipantId);
            handleTemplateChange(selectedTemplate || 'new');
            setView('list'); // Start with list view
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, participant]);

    useEffect(() => {
        if (selectedParticipant === 'all' || selectedParticipant === 'all_contacts' || selectedParticipant === 'all_contacts_exclude' || !participants || participants.length === 0) {
            setIsRecipientInternal(false);
        } else {
            const recipient = participants.find(p => p._id === selectedParticipant);
            setIsRecipientInternal(recipient?.intern === true);
        }
    }, [selectedParticipant, participants]);

    const fetchMessages = async () => {
        if (!tournament) return;
        try {
            const response = await axios.get(`/tournament/${tournament}/messages`);
            setMessages(response.data || []);
        } catch (error) {
            console.error("Fehler beim Laden der Nachrichten:", error);
        }
    };

    const fetchEmailTemplates = async () => {
        try {
            const response = await axios.get(`/api/EmailTemplate`);
            const templates = response.data?.data;
            if (Array.isArray(templates)) {
                setEmailTemplates(templates || []);
            } else {
                setEmailTemplates([]);
            }
        } catch (error) {
            console.error("Fehler beim Laden der Email-Templates:", error);
            toast.error("Fehler beim Laden der Email-Templates");
            setEmailTemplates([]);
        }
    };

    const resetForm = () => {
        setNewEmail({ subject: '', content: '' });
        setSelectedTemplate('new');
        setUseTemplate(false);
        setSelectedParticipant(participant?._id || 'all');
        setIsRecipientInternal(participant?.intern === true);
    };

    const handleTemplateChange = (eventOrValue) => {
        const selectedId = typeof eventOrValue === 'string' ? eventOrValue : eventOrValue.target.value;
        setSelectedTemplate(selectedId);
        setUseTemplate(false);
        if (selectedId === 'new') {
            setNewEmail({ subject: '', content: '' });
        } else {
            const template = Array.isArray(emailTemplates) ? emailTemplates.find(t => t._id === selectedId) : undefined;
            if (template) {
                setNewEmail({ subject: template.subject || '', content: template.content || '' });
            } else if (selectedId !== 'new') {
                setNewEmail({ subject: '', content: '' });
            }
        }
    };

    const handleUseTemplate = (checked) => {
        setUseTemplate(checked);
        if (!checked && selectedTemplate !== 'new') {
            const template = Array.isArray(emailTemplates) ? emailTemplates.find(t => t._id === selectedTemplate) : undefined;
            if (template) { setNewEmail({ subject: template.subject || '', content: template.content || '' }); }
        }
    };

    const sendEmail = async () => {
        if (isRecipientInternal) { toast.warn("E-Mails können nicht an interne Teams gesendet werden."); return; }
        const subjectIsEmpty = !newEmail.subject || !newEmail.subject.trim();
        const contentIsEmpty = !newEmail.content || !stripHtml(newEmail.content).trim();
        if (subjectIsEmpty || contentIsEmpty) { toast.error("Betreff und Inhalt dürfen nicht leer sein."); return; }
        setLoading(true);
        try {
            const payload = {
                emailTemplate: { subject: newEmail.subject, content: newEmail.content },
                emailTemplateId: selectedTemplate !== 'new' ? selectedTemplate : null,
                excludeTournamentParticipants: selectedParticipant === 'all_contacts_exclude'
            };

            let url;
            if (selectedParticipant === 'all') {
                url = `/mail/send-email/${tournament}`;
            } else if (selectedParticipant === 'all_contacts' || selectedParticipant === 'all_contacts_exclude') {
                url = `/mail/send-email-all-contacts/${tournament}`;
            } else {
                url = `/mail/send-email/${tournament}/${selectedParticipant}`;
            }

            await axios.post(url, payload);
            toast.success("E-Mail erfolgreich gesendet!");
            fetchMessages(); // Refresh history
            setView('list'); // Go back to list
        } catch (error) {
            console.error("Fehler beim Senden der E-Mail:", error);
            const errorMsg = error.response?.data?.message || "E-Mail konnte nicht gesendet werden.";
            toast.error(errorMsg);
        }
        finally { setLoading(false); }
    };

    const toggleArchiveMessage = async (message) => {
        try {
            const newStatus = !message.archived;
            await axios.put(`/tournament/${tournament}/messages/${message._id}/archive`, { archived: newStatus });
            toast.success(`Nachricht ${newStatus ? 'archiviert' : 'wiederhergestellt'}.`);
            fetchMessages();
            if (view === 'detail') setView('list');
        } catch (error) {
            console.error("Fehler beim Archivieren:", error);
            toast.error("Fehler beim Ändern des Archiv-Status.");
        }
    };

    const handleHide = () => { resetForm(); onHide(); };

    const insertPlaceholder = (placeholder) => {
        const quill = quillRef.current?.getEditor();
        if (quill) {
            const range = quill.getSelection(true);
            if (range) {
                quill.insertText(range.index, placeholder, 'user');
                quill.setSelection(range.index + placeholder.length, 0, 'user');
            } else {
                const length = quill.getLength();
                quill.insertText(length, placeholder, 'user');
                quill.setSelection(length + placeholder.length, 0, 'user');
            }
            quill.focus();
        }
    };

    const renderListView = () => {
        const filteredMessages = messages.filter(msg => showArchived ? msg.archived : !msg.archived);

        return (
            <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="text-muted mb-0">Verlauf</h6>
                    <div className="d-flex gap-2">
                        <button
                            className={`btn btn-sm ${showArchived ? 'btn-secondary' : 'btn-outline-secondary'}`}
                            onClick={() => setShowArchived(!showArchived)}
                            title={showArchived ? "Archiv ausblenden" : "Archiv anzeigen"}
                        >
                            {showArchived ? "Archiv ausblenden" : "Archiv anzeigen"}
                        </button>
                        <button className="btn btn-sm btn-danger d-flex align-items-center" onClick={() => { resetForm(); setView('compose'); }}>
                            <FiPlus className="me-1" /> Neue Nachricht
                        </button>
                    </div>
                </div>

                <div className="border rounded bg-white custom-scrollbar" style={{ height: '400px', overflowY: 'scroll' }}>
                    {filteredMessages.length === 0 ? (
                        <div className="d-flex flex-column justify-content-center align-items-center h-100 text-muted">
                            <FiClock size={30} className="mb-2" />
                            <p className="mb-0">{showArchived ? "Keine archivierten Nachrichten." : "Noch keine Nachrichten versendet."}</p>
                        </div>
                    ) : (
                        <div className="list-group list-group-flush">
                            {filteredMessages.map(msg => (
                                <button
                                    key={msg._id}
                                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-start border-0 border-bottom"
                                    onClick={() => { setSelectedMessage(msg); setView('detail'); }}
                                >
                                    <div className="ms-2 me-auto">
                                        <div className="fw-bold">{msg.subject}</div>
                                        <small className="text-muted">
                                            {msg.recipientType === 'all_participants' && 'An alle Teilnehmer'}
                                            {msg.recipientType === 'all_contacts' && 'An alle Kontakte (Werbung)'}
                                            {msg.recipientType === 'single_participant' && (
                                                msg.recipients && msg.recipients[0]?.teamname
                                                    ? `An: ${msg.recipients[0].teamname}`
                                                    : 'An einzelnen Teilnehmer'
                                            )}
                                            {msg.recipientType === 'custom' && 'Benutzerdefiniert'}
                                            {msg.recipientCount > 1 && ` (${msg.recipientCount} Empfänger)`}
                                        </small>
                                    </div>
                                    <span className="badge bg-light text-dark rounded-pill">
                                        {dayjs(msg.sentAt).format('DD.MM.YY HH:mm')}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <style>{`
                    .custom-scrollbar {
                        scrollbar-width: thin;
                        scrollbar-color: #bb0505 #f1f1f1;
                    }
                    .custom-scrollbar::-webkit-scrollbar {
                        width: 8px;
                    }
                    .custom-scrollbar::-webkit-scrollbar-track {
                        background: #f1f1f1;
                        border-radius: 4px;
                    }
                    .custom-scrollbar::-webkit-scrollbar-thumb {
                        background: #bb0505; 
                        border-radius: 4px;
                    }
                    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                        background: #a00404;
                    }
                `}</style>
            </>
        );
    };

    const renderDetailView = () => (
        <>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center">
                    <button className="btn btn-link text-dark p-0 me-2" onClick={() => setView('list')}>
                        <FiArrowLeft size={20} />
                    </button>
                    <h6 className="mb-0 fw-bold">Nachrichtendetails</h6>
                </div>
                {selectedMessage && (
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => toggleArchiveMessage(selectedMessage)}
                    >
                        {selectedMessage.archived ? "Wiederherstellen" : "Archivieren"}
                    </button>
                )}
            </div>

            {selectedMessage && (
                <div className="card border-0 bg-light">
                    <div className="card-body">
                        <div className="mb-3">
                            <small className="text-muted d-block">Betreff</small>
                            <span className="fw-bold">{selectedMessage.subject}</span>
                        </div>
                        <div className="mb-3">
                            <small className="text-muted d-block">Gesendet am</small>
                            <span>{dayjs(selectedMessage.sentAt).format('DD.MM.YYYY HH:mm')} Uhr</span>
                        </div>
                        <div className="mb-3">
                            <small className="text-muted d-block">Empfänger ({selectedMessage.recipientCount})</small>
                            <span>
                                {selectedMessage.recipientType === 'all_participants' && 'Alle Teilnehmer'}
                                {selectedMessage.recipientType === 'all_contacts' && 'Alle Kontakte (Werbung)'}
                                {selectedMessage.recipientType === 'single_participant' && 'Einzelner Teilnehmer'}
                            </span>
                            {selectedMessage.recipients && selectedMessage.recipients.length > 0 && (
                                <div className="mt-2 p-2 bg-white rounded border custom-scrollbar" style={{ maxHeight: '100px', overflowY: 'auto' }}>
                                    <small className="text-muted d-block mb-1">Empfängerliste:</small>
                                    {selectedMessage.recipients.map((r, idx) => (
                                        <div key={idx} className="small text-secondary">
                                            {r.teamname ? <span className="fw-semibold">{r.teamname}</span> : r.name} &lt;{r.email}&gt;
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div>
                            <small className="text-muted d-block mb-2">Inhalt</small>
                            <div className="bg-white p-3 rounded border" dangerouslySetInnerHTML={{ __html: selectedMessage.content }} />
                        </div>
                    </div>
                </div>
            )}
        </>
    );

    const renderComposeView = () => (
        <>
            <div className="d-flex align-items-center mb-3">
                <button className="btn btn-link text-dark p-0 me-2" onClick={() => setView('list')}>
                    <FiArrowLeft size={20} />
                </button>
                <h6 className="mb-0 fw-bold">Neue Nachricht verfassen</h6>
            </div>

            <Form.Group className="my-2">
                <Form.Label>An</Form.Label>
                <Form.Select value={selectedParticipant} onChange={(e) => setSelectedParticipant(e.target.value)}>
                    {participant && participant.intern !== true && (
                        <option key={participant._id} value={participant._id}>{participant.teamname}</option>
                    )}

                    <option value="all">Alle Teilnehmer (Turnier)</option>
                    <option value="all_contacts">Alle Kontakte (Werbung / Newsletter)</option>
                    <option value="all_contacts_exclude">Alle Kontakte (außer Turnierteilnehmer)</option>

                    {participants && Array.isArray(participants) && participants
                        .filter(p => (!participant || p._id !== participant._id) && p.intern !== true)
                        .map(p => (
                            <option key={p._id} value={p._id}>{p.teamname}</option>
                        ))}
                </Form.Select>
            </Form.Group>

            <Form.Group className="my-2">
                <Form.Label>Email Vorlage</Form.Label>
                <Form.Select value={selectedTemplate} onChange={handleTemplateChange} className='mb-4'>
                    <option value="new">Neue E-Mail schreiben</option>
                    {emailTemplates && Array.isArray(emailTemplates) && emailTemplates.map(template => (
                        <option key={template._id} value={template._id}>{template.title}</option>
                    ))}
                </Form.Select>
            </Form.Group>

            <div className='border px-3 py-3 rounded-4 bg-light'>
                <Form.Group>
                    <Form.Label className='fw-semibold'>Betreff</Form.Label>
                    <Form.Control
                        type="text"
                        value={newEmail.subject}
                        onChange={(e) => setNewEmail(prev => ({ ...prev, subject: e.target.value }))}
                        placeholder="Betreff..."
                        readOnly={selectedTemplate !== 'new' && !useTemplate}
                        className={(selectedTemplate !== 'new' && !useTemplate) ? 'bg-light' : ''}
                    />
                </Form.Group>

                <Form.Group className="mt-3">
                    <Form.Label className='fw-semibold'>Inhalt</Form.Label>
                    <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={newEmail.content}
                        placeholder="Schreibe hier..."
                        onChange={(value) => setNewEmail(prev => ({ ...prev, content: value }))}
                        readOnly={selectedTemplate !== 'new' && !useTemplate}
                        className={(selectedTemplate !== 'new' && !useTemplate) ? 'quill-readonly-visual-feedback bg-light' : 'bg-white'}
                    />
                    <style>{`
                .quill-readonly-visual-feedback .ql-editor {
                  background-color: #e9ecef !important;
                  cursor: not-allowed;
                }
              `}</style>

                    <div className="mt-2 pt-2 border-top d-flex flex-wrap justify-content-start align-items-center">
                        <div className='row'>
                            <div className='col-1 small text-muted'>Einfügen:</div>
                        </div>
                        <div className='col-11'>{placeholderMap.map(({ placeholder, alias }) => (
                            <button
                                key={placeholder}
                                type="button"
                                className="btn btn-outline-secondary btn-sm m-1 py-0 px-2"
                                onClick={() => insertPlaceholder(placeholder)}
                                title={`Platzhalter ${placeholder} einfügen`}
                                disabled={selectedTemplate !== 'new' && !useTemplate}
                            >
                                {alias}
                            </button>
                        ))}</div>
                    </div>
                </Form.Group>

                {selectedTemplate !== "new" && (
                    <Form.Group className="mt-3 ms-2">
                        <Form.Check
                            type="checkbox"
                            label="Vorlage bearbeiten"
                            checked={useTemplate}
                            onChange={(e) => handleUseTemplate(e.target.checked)}
                        />
                    </Form.Group>
                )}
            </div>
        </>
    );

    return (
        <Modal size="lg" show={show} onHide={handleHide} scrollable>
            <Modal.Header closeButton className='border border-0 rakete-rot'>
                <Modal.Title className='rakete-font fw-semibold text-uppercase fs-6'>Nachrichten Center</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {view === 'list' && renderListView()}
                {view === 'compose' && renderComposeView()}
                {view === 'detail' && renderDetailView()}
            </Modal.Body>
            <Modal.Footer className='border border-0'>
                {view === 'compose' && (
                    <button
                        className="text-center login-button text-uppercase rounded fw-semibold border border-0 fs-7"
                        onClick={sendEmail} type="button" disabled={loading || isRecipientInternal}
                        title={isRecipientInternal ? "Senden an interne Teams nicht möglich" : "E-Mail senden"} >
                        {loading ? 'SENDET...' : <><FiSend className="me-1" /> SENDEN</>}
                    </button>
                )}
                <button
                    className="text-center decline-button text-uppercase rounded fw-semibold fs-7"
                    onClick={handleHide} type="button" >
                    <FiXCircle className="me-1" /> Schließen
                </button>
            </Modal.Footer>
        </Modal>
    );
}

export default TournamentMessages;
